import {
  FunctionComponent,
  useReducer,
  useEffect,
  useRef,
} from "react";
import TextField, { TextFieldProps } from "./TextField";
import CheckBox, { CheckBoxProps } from "../CheckBox/CheckBox";
import Button from "../Button/Button";
import RadioButtonGroup, { RadioButtonGroupType } from "../Radio/RadioButtonGroup";
import FileUploadField, { FileUploadType } from "../FileUploadField/FileUploadField";
import { FormData, toFormData, UploadedFile } from "../form-data";
import { ComponentInput, PageInput } from "../../../lib/ssi/form.types";
import { ComponentId } from "../../../lib/types/ids";
import { ValidateResultInline } from "../../../lib/validations/validate";

type TextFieldsProps = {
  data: PageInput;
  onDataSelected: (item: FormData) => void;
  onBack?: () => void;
  resolveExpression: (expression: string) => unknown;
  errors: Record<string, ValidateResultInline>;
  isActive: boolean;
  nextPage?: string;
  isLoading?: boolean;
};

type InputValue = string | boolean | number | UploadedFile;

type FormInput = Record<ComponentId, InputValue>;

const TextFields: FunctionComponent<TextFieldsProps> = ({
  data,
  onDataSelected,
  onBack,
  resolveExpression,
  errors,
  isActive,
  nextPage,
  isLoading,
}) => {
  const initialState: FormInput = {};
  data.components.forEach((input) => {
    const id = input.id;
    initialState[id] = "";
  });
  const [formInput, setFormInput] = useReducer(
    (state: FormInput, newState: FormInput) => ({ ...state, ...newState }),
    initialState
  );
  const ref = useRef(formInput);

  useEffect(() => {
    ref.current = formInput;
  }, [formInput]);

  useEffect(() => {
    if (isActive) {
      document.addEventListener("keydown", onContinue);
      return () => {
        document.removeEventListener("keydown", onContinue);
      };
    }
    return;
  }, [isActive]);

  const onContinue = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      onSelectData();
    }
  };

  const onInputChange = (value: InputValue, id: string) => {
    if (value === true) {
      setFormInput({ [id]: "accepted" });
    } else if (value === false) {
      setFormInput({ [id]: false });
    } else {
      setFormInput({ [id]: value });
    }
  };

  const content: JSX.Element[] = [];
  data.components.forEach((el: ComponentInput) => {
    if (el.condition && !resolveExpression(el.condition)){
      // Entferne eventuell zuvor gegebene Antworten für diese Komponente.
      // z. B. Zubuchungen und der Abbuchungstag sind abhängig von Tarifen
      delete formInput[el.id];
      return;
    }

    let element;
    if (el.type === "checkbox") {
      const checkboxData: CheckBoxProps = {
        ...el,
        checked: !!formInput[el.id],
        setChecked: onInputChange,
        error: !!errors[el.id],
        errorText: errors[el.id]?.text,
      };
      element = (
        <CheckBox
          {...checkboxData}
          key={el.id}
        />
      );
    } else if (el.type === "file") {
      const fileUploadData: FileUploadType = {
        ...el,
        name: ((x) => typeof x === "string" ? x : "")(formInput[el.id]),
        setFile: onInputChange,
        error: !!errors[el.id],
        errorText: errors[el.id]?.text,
      };
      element = (
        <FileUploadField
          {...fileUploadData}
        />
      );
    } else if (el.type === "radio") {
      const radioButtonData: RadioButtonGroupType = {
        ...el,
        active: ((active) => {
          switch (typeof active) {
            case "boolean":
            case "string":
            case "number":
              return active;

            default:
              return undefined;
          }
        })(formInput[el.id]),
        setValue: onInputChange,
        error: !!errors[el.id],
        errorText: errors[el.id]?.text,
        properties: el.properties,
      };
      element = (
        <RadioButtonGroup
          {...radioButtonData}
          key={el.id}
        />
      );
    } else {
      const textfieldProps: TextFieldProps = {
        ...el,
        autoFocus: false,
        name: ((x) => typeof x === "string" ? x : "")(formInput[el.id]),
        onChange: onInputChange,
        error: !!errors[el.id],
        errorText: errors[el.id]?.text,
      };
      element = (
        <TextField
          {...textfieldProps}
          key={el.id}
        />
      );
    }
    content[el.index] = (
      <div className="mb-6" key={el.id}>
        {element}
      </div>
    );
  });

  const onSelectData = () => {
    onDataSelected(
      toFormData("input", data, ref.current)
    );
  };
  return (
    <div className="w-full max-w-3xl">
      {content}
      <div className="flex flex-wrap justify-center gap-10">
        {onBack ? (
          <Button variant="outline" onPress={() => onBack()}>
            Zurück
          </Button>
        ) : null}
        <Button
          variant="primary"
          onPress={() => onSelectData()}
          isLoading={isLoading}
        >
          {nextPage ? "Weiter" : "Anmelden"}
        </Button>
      </div>
    </div>
  );
};

export default TextFields;
